/* import __COLOCATED_TEMPLATE__ from './team-select.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { inject as service } from '@ember/service';
import { generateSafeEmoji } from 'embercom/lib/emoji';
import type AppService from 'embercom/services/app-service';
import type IntlService from 'ember-intl/services/intl';
import type Admin from 'embercom/models/admin';

interface TeamSelectItem {
  text?: string;
  value: string | null;
  isSelected?: boolean;
  isDisabled?: boolean;
  component: string;
  componentAttrs?: {
    teamAvatar: string;
  };
}

interface Signature {
  Element: HTMLDivElement;
  Args: {
    selectedTeams: string[];
    onSelectionChanged: (newSelection: string[]) => void;
  };
}

export default class TeamSelect extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intl: IntlService;

  get sortedTeams(): Admin[] {
    return this.appService.app.teams.sortBy('name') as Admin[];
  }

  get selectedAndRedactedTeamIds(): string[] {
    return this.sortedTeams
      .filter((team) => this.args.selectedTeams.includes(team.id) && team.is_redacted)
      .map((team) => team.id);
  }

  get teamSelectItems(): TeamSelectItem[] {
    if (this.sortedTeams.length === 0) {
      return [
        {
          value: null,
          isDisabled: true,
          component: 'settings/saved-replies/team-select-empty-item',
        },
      ];
    }

    let filteredTeams = this.sortedTeams.filter((team) => !team.is_redacted);

    return filteredTeams.map((team) => ({
      text: team.name,
      value: team.id,
      isSelected: this.args.selectedTeams.includes(team.id),
      component: 'settings/saved-replies/team-select-item',
      componentAttrs: { teamAvatar: generateSafeEmoji(team.avatar_emoji) },
    }));
  }

  get openerLabel(): string {
    if (isEmpty(this.args.selectedTeams)) {
      return this.intl.t('inbox.manage-macros.team-select.opener-label');
    } else {
      return this.args.selectedTeams
        .map((id) => {
          let team = this.sortedTeams.find((team) => parseInt(team.id, 10) === parseInt(id, 10));
          if (team) {
            return `${team.avatar_emoji} ${team.name}`;
          } else {
            return this.intl.t('new-settings.helpdesk.macros.deleted-team');
          }
        })
        .join(', ');
    }
  }

  @action selectionChanged(newSelection: string[]): void {
    this.args.onSelectionChanged([...newSelection, ...this.selectedAndRedactedTeamIds]);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Settings::SavedReplies::TeamSelect': typeof TeamSelect;
    'settings/saved-replies/team-select': typeof TeamSelect;
  }
}
